import React from "react";
import clsx from "clsx";
import { isFalsy, isTruthy } from "@/utils/guards";

import { Button, Toggle, Checkbox, Icon } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import Loader from "@/ui/atoms/Loader";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import type { TRowType, TTableLocation } from "@/apis/odin/apiODIN";
import type { TOptionWithType } from "@/store/accounts/accountTypes";
import {
    EDITED_FIELD_SUFFIX,
    editedFieldKey,
    originalFieldKey,
} from "@/store/tableExtractions/formatUtils";
import GridCellEditPanel from "@/ui/organisms/GridCellEditPanel";

import type { GridApi } from "@/ui/molecules/Grid";

import type { ExtractionType } from "@/ui/organisms/dealTypes";

import type { GridMappingsProps } from "@/ui/organisms/GridMappings";
import GridMappings from "@/ui/organisms/GridMappings";
import type { ExtractionsErrorListProps } from "@/ui/organisms/ExtractionsSidebar/ExtractionsErrorList";
import { CloseExtractionModal } from "@/ui/organisms/CloseExtractionModal";

import type { LayoutFullScreenWithCollapsibleSidebarProps } from "@/ui/layouts/LayoutFullScreenWithCollapsibleSidebar";
import LayoutFullScreenWithCollapsibleSidebar, {
    testIds as layoutFullScreenWithCollapsibleSidebarTestIds,
} from "@/ui/layouts/LayoutFullScreenWithCollapsibleSidebar";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import LayoutFullScreenOverlay from "@/ui/layouts/LayoutFullScreenOverlay";
import LayoutBoxCentered from "@/ui/layouts/LayoutBoxCentered";
import ProgressBar from "@/ui/molecules/ProgressBar";
import PillDropdown from "@/ui/molecules/PillDropdown";
import type { PillDropdownOption } from "@/ui/molecules/PillDropdown";
import Typography from "@/ui/atoms/Typography";
import Dropdown, { type DropdownOption } from "@/ui/molecules/Dropdown";
import { ResetRowChangesModal } from "@/ui/organisms/ResetRowChangesModal/ResetRowChangesModal";
import VerticalDivider from "@/ui/molecules/VerticalDivider";

const getTypedEmptyArray = <T,>() => [] as T[];

const LlmExtractionOverlay: React.FC<{
    fileName: string;
    progress: number;
    progressMessage: string | null;
}> = ({ fileName, progress, progressMessage }) => {
    return (
        <LayoutFullScreenOverlay
            backgroundColor="rgba(0, 0, 0, 0.5)"
            opacity={1}
        >
            <LayoutFillParentCentered>
                <LayoutBoxCentered
                    sx={{
                        width: "45%",
                        minWidth: "500px",
                        "@media (max-width: 600px)": {
                            width: "85%",
                        },
                    }}
                >
                    <Box sx={{ padding: "80px" }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    fontSize: "2rem",
                                    fontWeight: 600,
                                    color: "var(--text-color-dark)",
                                }}
                            >
                                The file is being processed...
                            </Box>
                            <Box
                                component={"p"}
                                sx={{
                                    color: "var(--proda-black-10)",
                                    fontWeight: "400 !important",
                                    width: "55%",
                                    marginTop: "16px",
                                    marginBottom: "16px",
                                }}
                            >
                                Do not close the window while the file is being
                                processed.
                            </Box>
                        </Box>
                        <ProgressBar
                            label={fileName}
                            subLabel={progressMessage}
                            progressPercentage={progress}
                        />
                    </Box>
                </LayoutBoxCentered>
            </LayoutFillParentCentered>
        </LayoutFullScreenOverlay>
    );
};

const rowTypeOptions: DropdownOption<TRowType>[] = [
    {
        label: "Account",
        value: "account",
    },
    {
        label: "Category",
        value: "category",
    },
    {
        label: "Total",
        value: "total",
    },
];

export type ViewExtractionMappingsParams = {
    useState: (params: {
        extractionId: number;
        projectId?: number;
        databaseId?: number;
    }) => {
        showLoadingOverlay: boolean;
        disableNextButton: boolean;
        isAccountsLoading: boolean;
        isAccountsError: boolean;
        isAccountsReady: boolean;
        isExtractionLoading: boolean;
        isExtractionError: boolean;
        isExtractionReady: boolean;
        isTableChangePending: boolean;
        isTableChangeError: boolean;
        columnDefsOperatingStatements:
            | GridMappingsProps["columnDefsOperatingStatements"]
            | null;
        extractionStatus: "draft" | "completed" | null;
        rowData: GridMappingsProps["rowData"] | null;
        totalRows: GridMappingsProps["totalRows"] | null;
        categoryRows: GridMappingsProps["categoryRows"] | null;
        optionsAccountsWithType: TOptionWithType[];
        dealName: string | null;
        alteredRowsIds: number[] | null;
        totalNetIncomeOriginal: GridMappingsProps["totalNetIncomeOriginal"];
        totalNetIncomeAltered: { [key: string]: number } | null;
        errorGroups: ExtractionsErrorListProps["errors"];
        closeExtractionModalOpen: boolean;
        extractionType: ExtractionType | null;
        selectedRows: number[];
        selectedColumns: number[];
        editMode: "off" | "rows" | "columns";
        allFoundTables: Array<TTableLocation> | null;
        isColumnStatePending: boolean;
        fileName: string | null;
        isAiExtractionEnabled: boolean;
        isFromLlm: boolean;
        highlightedTargetRowIds: Array<number | string>;
    };
    useCases: () => {
        onMappingsAltered: (params: {
            changes: { rowId: number; accountCode: string | null }[];
            extractionId: number;
            accountOptions: TOptionWithType[];
        }) => void;
        onRowTypeChanged: (params: {
            rowId: number;
            rowType: TRowType;
            extractionId: number;
            projectId: number;
        }) => void;
        onRowSignFlipped: (params: {
            rowId: number;
            extractionId: number;
            projectId: number;
            flipped: boolean;
        }) => void;
        onDocumentTargetRowSelected: (params: {
            rowId: number | null;
            extractionId: number;
        }) => void;
        onCellValuesEdited: (params: {
            extractionId: number;
            cells: { rowId: number; columnId: number; amount: number | null }[];
        }) => void;
        onCellValuesReverted: (params: {
            extractionId: number;
            cells: { rowId: number; columnId: number }[];
        }) => void;
        onMount: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => void;
        onUnmount: () => void;
        onCloseWithoutSaveRequested: (params: {
            projectId: number;
            databaseId: number;
        }) => void;
        onCloseAndSaveRequested: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            extractionStatus: "draft" | "completed";
        }) => void;
        onNextClicked: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => Promise<void>;
        onExtractionIsNotDraft: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => void;
        onDiscardExtraction: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => void;
        setCloseExtractionModalOpenState: (state: boolean) => void;
        onTableChange: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            table: TTableLocation;
        }) => void;
        onUpdateExtractionType: (params: {
            extractionId: number;
            extractionType: ExtractionType;
        }) => void;
        setSelectedRows: (rowIds: number[]) => void;
        setSelectedColumns: (columnIds: number[]) => void;
        updateSelectedRowsType: (
            extractionId: number,
            rowType: TRowType,
        ) => void;
        flipSelectedRowsSign: (extractionId: number, flipped: boolean) => void;
        resetRowChanges: (params: {
            extractionId: number;
            rowIds: number[];
        }) => Promise<void>;
        setEditMode: (editMode: "off" | "rows" | "columns") => void;
        setColumnState: (
            extractionId: number,
            state: boolean,
            columnId?: number,
        ) => Promise<void>;
        batchSetColumnState: (
            extractionId: number,
            columnIds: number[],
            state: boolean,
        ) => Promise<void>;
    };
    useLlmReExtraction: () => {
        llmReExtractStatus: "idle" | "pending" | "resolved" | "rejected";
        llmReExtractProgress: number | null;
        llmReExtractMessage: string | null;
        llmReExtractError: string | null;
        llmReExtract: (params: {
            extractionId: number;
            projectId: number;
            databaseId: number;
        }) => Promise<void>;
        resetLlmReExtractState: () => void;
    };
    ComponentSidebar: React.FC<{
        extractionId: number;
        projectId?: number;
        databaseId?: number;
        gridApi: GridApi | null;
        description: string;
        allFoundTables: Array<TTableLocation> | null;
        canChangeTable?: boolean;
        onTableChange: (params: { table: TTableLocation }) => void;
        onLlmReExtract?: () => void;
        llmReExtractStatus?: "idle" | "pending" | "resolved" | "rejected";
        isFromLlm: boolean;
    }>;
    ComponentConcurrentUsersIndicator: React.FC;
    ComponentConcurrentUsersBanner: React.FC;
    ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount: React.FC<{
        extractionId: number;
        resourceKey: "extraction-mapping";
    }>;
};

export type ViewExtractionMappingsProps = Omit<
    Omit<
        LayoutFullScreenWithCollapsibleSidebarProps,
        | "headerText"
        | "sidebarHeader"
        | "sidebarContent"
        | "isSidebarCollapsed"
        | "onSidebarToggle"
        | "onClose"
        | "footerContent"
    >,
    "children"
> & {
    projectId: number;
    databaseId: number;
    extractionId: number;
};

export const testIds = {
    main: "ViewExtractionMappings",
    iconClose: layoutFullScreenWithCollapsibleSidebarTestIds.iconClose,
    buttonNext: "ViewExtractionMappings-button-next",
    toggleEdit: "ViewExtractionMappings-toggle-edit",
    buttonEditRows: "ViewExtractionMappings-button-edit-rows",
    buttonEditColumns: "ViewExtractionMappings-button-edit-columns",
};

const FooterVerticalDivider = (): JSX.Element => (
    <VerticalDivider
        sx={{
            marginLeft: "16px",
            marginRight: "16px",
            width: "1px",
            flexShrink: 0,
        }}
    />
);

export const createViewExtractionMappings = ({
    useState,
    useCases,
    useLlmReExtraction,
    ComponentSidebar = () => null,
    ComponentConcurrentUsersIndicator = () => null,
    ComponentConcurrentUsersBanner = () => null,
    ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount = () => null,
}: ViewExtractionMappingsParams) => {
    const ViewExtractionMappings: React.FC<ViewExtractionMappingsProps> & {
        setUpPageTestDependencies: (params: { gridApi: GridApi }) => void;
        cleanUpPageTestDependencies: () => void;
    } = props => {
        const {
            className,
            projectId,
            databaseId,
            extractionId,
            ...otherProps
        } = props;

        const {
            onMount,
            onUnmount,
            onMappingsAltered,
            onRowTypeChanged,
            onRowSignFlipped,
            onDocumentTargetRowSelected,
            onCellValuesEdited,
            onCellValuesReverted,
            onCloseAndSaveRequested,
            onCloseWithoutSaveRequested,
            onNextClicked,
            onExtractionIsNotDraft,
            onDiscardExtraction,
            setCloseExtractionModalOpenState,
            onTableChange,
            onUpdateExtractionType,
            setSelectedRows,
            setSelectedColumns,
            updateSelectedRowsType,
            flipSelectedRowsSign,
            resetRowChanges,
            setEditMode,
            setColumnState,
            batchSetColumnState,
        } = useCases();

        const {
            showLoadingOverlay,
            disableNextButton,
            extractionStatus,
            isExtractionLoading,
            isExtractionError,
            isExtractionReady,
            optionsAccountsWithType,
            isAccountsLoading,
            isAccountsError,
            isAccountsReady,
            columnDefsOperatingStatements,
            rowData,
            totalRows,
            categoryRows,
            dealName,
            alteredRowsIds,
            totalNetIncomeOriginal,
            totalNetIncomeAltered,
            errorGroups,
            closeExtractionModalOpen,
            isTableChangePending,
            isTableChangeError,
            extractionType,
            selectedRows,
            selectedColumns,
            editMode,
            allFoundTables,
            isColumnStatePending,
            fileName,
            isAiExtractionEnabled,
            isFromLlm,
            highlightedTargetRowIds,
        } = useState({ extractionId, projectId, databaseId });
        const {
            llmReExtractStatus,
            llmReExtractProgress,
            llmReExtractMessage,
            llmReExtractError,
            llmReExtract,
            resetLlmReExtractState,
        } = useLlmReExtraction();
        const allRows = React.useMemo(
            () => [
                ...(rowData ?? []),
                ...(totalRows ?? []),
                ...(categoryRows ?? []),
            ],
            [rowData, totalRows, categoryRows],
        );
        const rowsById = React.useMemo(() => {
            return Object.fromEntries(allRows.map(r => [r.rowId, r]));
        }, [allRows]);

        const [isResetModalOpen, setIsResetModalOpen] = React.useState(false);
        const [selectedValueCell, setSelectedValueCell] = React.useState<{
            rowId: number;
            columnId: number;
            field: string;
        } | null>(null);
        const onValueCellSelected = React.useCallback<
            NonNullable<GridMappingsProps["onValueCellSelected"]>
        >(cell => {
            setSelectedValueCell(current => {
                if (cell === null) return current === null ? current : null;
                if (
                    current !== null &&
                    current.rowId === cell.rowId &&
                    current.columnId === cell.columnId &&
                    current.field === cell.field
                ) {
                    return current;
                }
                return cell;
            });
        }, []);

        const isYearToDate = extractionType === "year_to_date";

        const handleResetToOriginal = React.useCallback(() => {
            setIsResetModalOpen(true);
        }, []);

        const isDraft = extractionStatus === "draft";
        const isCompleted = extractionStatus === "completed";
        const allowValueEditing = isDraft && isFromLlm;
        const canChangeTable = !isFromLlm || isAiExtractionEnabled;

        const selectedCellPanel = React.useMemo(() => {
            if (!allowValueEditing || !selectedValueCell) return null;
            const row = rowsById[selectedValueCell.rowId];
            if (!row || row.rowType !== "account") return null;
            const { field } = selectedValueCell;
            const current = typeof row[field] === "number" ? row[field] : null;
            const originalRaw = row[originalFieldKey(field)];
            return {
                accountName: row.originalAccountName,
                columnHeader:
                    columnDefsOperatingStatements?.find(
                        column => column.field === field,
                    )?.headerName ?? null,
                current,
                original: typeof originalRaw === "number" ? originalRaw : null,
                isEdited: row[editedFieldKey(field)] === true,
            };
        }, [
            allowValueEditing,
            selectedValueCell,
            rowsById,
            columnDefsOperatingStatements,
        ]);

        const pageTitle = `${isTruthy(dealName) ? `${dealName} – ` : ""}Operating Statement`;
        const sidebarDescription = "Review and adjust the extracted data.";
        const sidebarHeader = "1/2 Review Mappings";

        const handleExtractionIsNotDraft = React.useCallback(() => {
            if (!isCompleted) return;
            onExtractionIsNotDraft({
                projectId,
                databaseId,
                extractionId,
            });
        }, [
            projectId,
            databaseId,
            extractionId,
            isCompleted,
            onExtractionIsNotDraft,
        ]);
        React.useEffect(handleExtractionIsNotDraft, [
            handleExtractionIsNotDraft,
        ]);

        const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
        const onGridMappingsReadyHandler = React.useCallback<
            Exclude<GridMappingsProps["onGridReady"], undefined>
        >(({ api }) => {
            setGridApi(api);
        }, []);

        React.useEffect(() => {
            if (gridApi && editMode !== "rows") {
                const selectedNodes = gridApi.getSelectedNodes();
                gridApi.setNodesSelected({
                    nodes: selectedNodes,
                    newValue: false,
                });
            }
        }, [editMode, gridApi]);
        const prevTargetRowIdsRef = React.useRef<Array<number | string>>([]);
        React.useEffect(() => {
            if (!gridApi) return;
            const prevIds = prevTargetRowIdsRef.current;
            prevTargetRowIdsRef.current = highlightedTargetRowIds;
            // Union with the previous ids so cleared highlights repaint too.
            const affectedNodes = [
                ...new Set([...prevIds, ...highlightedTargetRowIds]),
            ]
                .map(id => gridApi.getRowNode(String(id)))
                .filter(isTruthy);
            if (affectedNodes.length === 0) return;
            gridApi.redrawRows({ rowNodes: affectedNodes });
            const firstRowId = highlightedTargetRowIds[0];
            if (firstRowId !== undefined) {
                const node = gridApi.getRowNode(String(firstRowId));
                if (node?.rowIndex !== null && node?.rowIndex !== undefined) {
                    gridApi.ensureIndexVisible(node.rowIndex, "middle");
                }
            }
        }, [gridApi, highlightedTargetRowIds]);
        // A row-type change alters styling (bold/total background) without
        // changing cell values, so AG Grid's change detection skips the cells;
        // force a redraw of rows whose type changed.
        const prevRowTypesRef = React.useRef<Record<string, string>>({});
        React.useEffect(() => {
            if (!gridApi) return;
            const prev = prevRowTypesRef.current;
            const next = Object.fromEntries(
                allRows.map(row => [String(row.rowId), row.rowType]),
            );
            prevRowTypesRef.current = next;
            const changedNodes = Object.keys(next)
                .filter(id => prev[id] !== undefined && prev[id] !== next[id])
                .map(id => gridApi.getRowNode(id))
                .filter(isTruthy);
            if (changedNodes.length === 0) return;
            gridApi.redrawRows({ rowNodes: changedNodes });
        }, [gridApi, allRows]);
        const onGridMappingsFirstDataRendered = React.useCallback<
            Exclude<GridMappingsProps["onFirstDataRendered"], undefined>
        >(({ api }) => {
            ViewExtractionMappings.setUpPageTestDependencies({ gridApi: api });
        }, []);

        const [isSidebarCollapsed, setIsSidebarCollapsed] =
            React.useState(false);

        const yearlyViewModeOptions = React.useMemo<
            PillDropdownOption<ExtractionType>[]
        >(
            () => [
                {
                    label: "Budget",
                    value: "operating_statement_yearly_budget",
                    icon: "chart-mixed-up-circle-dollar",
                },
                {
                    label: "Trailing",
                    value: "operating_statement_yearly_actual",
                    icon: "file-spreadsheet",
                },
                {
                    label: "Actual to Budget",
                    value: "operating_statement_actual_to_budget",
                    icon: "chart-mixed-up-circle-dollar",
                    disabled: true,
                    hidden: true,
                },
            ],
            [],
        );

        const monthlyViewModeOptions = React.useMemo<
            PillDropdownOption<ExtractionType>[]
        >(
            () => [
                {
                    label: "Budget",
                    value: "operating_statement_monthly_budget",
                    icon: "chart-mixed-up-circle-dollar",
                },
                {
                    label: "Trailing",
                    value: "operating_statement_monthly_actual",
                    icon: "file-spreadsheet",
                },
                {
                    label: "Actual to Budget",
                    value: "operating_statement_actual_to_budget",
                    icon: "chart-mixed-up-circle-dollar",
                    disabled: true,
                    hidden: true,
                },
            ],
            [],
        );

        const viewModeOptions = React.useMemo(() => {
            if (isFalsy(extractionType)) {
                return [];
            }
            if (extractionType.includes("yearly")) {
                return yearlyViewModeOptions;
            }
            return monthlyViewModeOptions;
        }, [extractionType, yearlyViewModeOptions, monthlyViewModeOptions]);

        React.useEffect(() => {
            void onMount({ projectId, databaseId, extractionId });

            return () => {
                ViewExtractionMappings.cleanUpPageTestDependencies();
                onUnmount();
            };
        }, [projectId, databaseId, extractionId, onMount, onUnmount]);

        const onChangeAccountsHandler = React.useCallback<
            GridMappingsProps["onChangeAccounts"]
        >(
            changes => {
                onMappingsAltered({
                    changes,
                    extractionId,
                    accountOptions: optionsAccountsWithType,
                });
            },
            [onMappingsAltered, extractionId, optionsAccountsWithType],
        );

        const onChangeRowTypeHandler = React.useCallback<
            GridMappingsProps["onChangeRowType"]
        >(
            ({ rowId, rowType }) => {
                onRowTypeChanged({
                    rowId,
                    rowType,
                    extractionId,
                    projectId,
                });
            },
            [onRowTypeChanged, projectId, extractionId],
        );

        const onFlipRowSignHandler = React.useCallback<
            GridMappingsProps["onFlipRowSign"]
        >(
            ({ rowId }) => {
                const row = rowsById[rowId];
                onRowSignFlipped({
                    rowId,
                    extractionId,
                    projectId,
                    flipped: !(
                        (row && "flipRowSign" in row && row.flipRowSign) ??
                        false
                    ),
                });
            },
            [onRowSignFlipped, projectId, extractionId, rowsById],
        );

        const onSetDocumentTargetRowHandler = React.useCallback<
            GridMappingsProps["onSetDocumentTargetRow"]
        >(
            ({ rowId }) => {
                onDocumentTargetRowSelected({
                    rowId,
                    extractionId,
                });
            },
            [onDocumentTargetRowSelected, extractionId],
        );

        const onCellValuesEditedHandler = React.useCallback<
            GridMappingsProps["onCellValuesEdited"]
        >(
            cells => {
                onCellValuesEdited({ extractionId, cells });
            },
            [onCellValuesEdited, extractionId],
        );

        const onCellValuesRevertedHandler = React.useCallback(
            (cells: { rowId: number; columnId: number }[]) => {
                onCellValuesReverted({ extractionId, cells });
            },
            [onCellValuesReverted, extractionId],
        );

        // Stable identity for column definition callbacks.
        const setColumnStateRef = React.useRef(setColumnState);
        setColumnStateRef.current = setColumnState;
        const stableSetColumnState = React.useCallback(
            (extractionId: number, state: boolean, columnId?: number) =>
                setColumnStateRef.current(extractionId, state, columnId),
            [],
        );

        const flipRowSignCheckbox = React.useMemo<{
            checked: boolean;
            indeterminate?: boolean;
        }>(() => {
            if (selectedRows.length === 0) {
                return { checked: false };
            }
            const values = selectedRows.map(rowId => {
                const row = rowsById[rowId];
                return (
                    (row && "flipRowSign" in row && row.flipRowSign) ?? false
                );
            });
            return values.every(v => v === true)
                ? { checked: true }
                : values.every(v => v === false)
                  ? { checked: false }
                  : { checked: true, indeterminate: true };
        }, [selectedRows, rowsById]);

        const showResetChangesButton = React.useMemo<boolean>(() => {
            const rowHasChanges = (row: {
                flipRowSign?: boolean | null;
                isDocumentTarget?: boolean | null;
                originalRowType: string;
                rowType: string;
                [key: string]: unknown;
            }): boolean =>
                (row?.flipRowSign ?? false) ||
                (row?.isDocumentTarget ?? false) ||
                row.rowType !== row.originalRowType ||
                Object.keys(row).some(
                    key =>
                        key.endsWith(EDITED_FIELD_SUFFIX) && row[key] === true,
                );
            if (selectedRows.length === 0) return allRows.some(rowHasChanges);
            return selectedRows.some(rowId => {
                const row = rowsById[rowId];
                return row ? rowHasChanges(row) : false;
            });
        }, [allRows, rowsById, selectedRows]);

        const hasDisabledColumns = React.useMemo(
            () =>
                columnDefsOperatingStatements?.some(
                    col => col.isDisabled === true,
                ) ?? false,
            [columnDefsOperatingStatements],
        );

        const onToggleColumnSelection = React.useCallback(
            (columnId: number, checked: boolean) => {
                if (checked) {
                    setSelectedColumns([...selectedColumns, columnId]);
                } else {
                    setSelectedColumns(
                        selectedColumns.filter(id => id !== columnId),
                    );
                }
            },
            [selectedColumns, setSelectedColumns],
        );

        const columnActionOptions: DropdownOption<string>[] = [
            { label: "Show Selected Columns", value: "show" },
            { label: "Hide Selected Columns", value: "hide" },
        ];

        const displayError = (msg: string) => (
            <LayoutFullScreenWithCollapsibleSidebar
                headerText={pageTitle}
                sidebarHeader={sidebarHeader}
                onClose={() =>
                    onCloseWithoutSaveRequested({ projectId, databaseId })
                }
            >
                <LayoutFillParentCentered>
                    <ErrorMessage>{msg}</ErrorMessage>
                </LayoutFillParentCentered>
            </LayoutFullScreenWithCollapsibleSidebar>
        );

        if (isTableChangeError) {
            return displayError("There was an error changing the table.");
        }

        if (isExtractionError) {
            return displayError("There was an error loading the extraction.");
        }

        if (isAccountsError) {
            return displayError(
                "There was an error loading the chart of accounts.",
            );
        }

        if (
            isAccountsLoading ||
            isExtractionLoading ||
            !isAccountsReady ||
            !isExtractionReady ||
            isTableChangePending ||
            isColumnStatePending
        ) {
            return (
                <LayoutFullScreenWithCollapsibleSidebar
                    headerText={pageTitle}
                    sidebarHeader={sidebarHeader}
                >
                    <LayoutFillParentCentered>
                        <Loader />
                    </LayoutFillParentCentered>
                </LayoutFullScreenWithCollapsibleSidebar>
            );
        }

        if (!isDraft) {
            return (
                <LayoutFullScreenWithCollapsibleSidebar
                    headerText={pageTitle}
                    sidebarHeader={sidebarHeader}
                >
                    <LayoutFillParentCentered>
                        <Loader />
                    </LayoutFillParentCentered>
                </LayoutFullScreenWithCollapsibleSidebar>
            );
        }

        return (
            <>
                <ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount
                    extractionId={extractionId}
                    resourceKey={"extraction-mapping"}
                />
                {llmReExtractError !== null && (
                    <LayoutFullScreenOverlay
                        backgroundColor="rgba(0, 0, 0, 0.5)"
                        opacity={1}
                    >
                        <LayoutFillParentCentered>
                            <LayoutBoxCentered
                                sx={{
                                    width: "45%",
                                    minWidth: "500px",
                                    "@media (max-width: 600px)": {
                                        width: "85%",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        padding: "32px",
                                        textAlign: "center",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                    }}
                                >
                                    <Icon
                                        icon="triangle-exclamation"
                                        size={48}
                                        color="var(--proda-yellow-120)"
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginY: "24px",
                                            maxWidth: "600px",
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: "var(--proda-black)",
                                                fontWeight: 700,
                                            }}
                                        >
                                            An error occurred
                                        </Typography>
                                        <Typography
                                            variant="body3"
                                            sx={{
                                                color: "var(--proda-black-10)",
                                            }}
                                        >
                                            {llmReExtractError}
                                        </Typography>
                                    </Box>
                                    <Button
                                        pattern="secondary"
                                        size="large"
                                        onClick={resetLlmReExtractState}
                                        style={{
                                            minWidth: "120px",
                                            padding: "0px 16px",
                                        }}
                                    >
                                        Close
                                    </Button>
                                </Box>
                            </LayoutBoxCentered>
                        </LayoutFillParentCentered>
                    </LayoutFullScreenOverlay>
                )}
                {(showLoadingOverlay || llmReExtractStatus === "pending") &&
                    (llmReExtractStatus === "pending" ? (
                        <LlmExtractionOverlay
                            fileName={fileName ?? ""}
                            progress={llmReExtractProgress ?? 0}
                            progressMessage={llmReExtractMessage}
                        />
                    ) : (
                        <LayoutFullScreenOverlay>
                            <LayoutFillParentCentered>
                                <Loader />
                            </LayoutFillParentCentered>
                        </LayoutFullScreenOverlay>
                    ))}
                <CloseExtractionModal
                    showModal={closeExtractionModalOpen}
                    onConfirm={() =>
                        void onCloseAndSaveRequested({
                            projectId,
                            extractionId,
                            extractionStatus,
                            databaseId,
                        })
                    }
                    onCancel={() => setCloseExtractionModalOpenState(false)}
                    onDiscard={() =>
                        void onDiscardExtraction({
                            projectId,
                            databaseId,
                            extractionId,
                        })
                    }
                />
                <ResetRowChangesModal
                    showModal={isResetModalOpen}
                    onCancel={() => setIsResetModalOpen(false)}
                    onConfirm={() => {
                        void resetRowChanges({
                            extractionId,
                            rowIds: selectedRows,
                        });
                        setIsResetModalOpen(false);
                    }}
                />
                <LayoutFullScreenWithCollapsibleSidebar
                    className={clsx(testIds.main, className)}
                    data-testid={testIds.main}
                    {...otherProps}
                    headerText={pageTitle}
                    headerLeftMenu={
                        isDraft &&
                        !isYearToDate && (
                            <PillDropdown
                                options={viewModeOptions}
                                value={extractionType}
                                disabled={
                                    extractionType ===
                                    "operating_statement_actual_to_budget"
                                }
                                onChange={_extractionType => {
                                    onUpdateExtractionType({
                                        extractionId,
                                        extractionType: _extractionType,
                                    });
                                }}
                                colour="proda-purple"
                            />
                        )
                    }
                    sidebarHeader={sidebarHeader}
                    sidebarContent={
                        <>
                            {selectedCellPanel && (
                                <GridCellEditPanel
                                    accountName={selectedCellPanel.accountName}
                                    columnHeader={
                                        selectedCellPanel.columnHeader
                                    }
                                    original={selectedCellPanel.original}
                                    current={selectedCellPanel.current}
                                    isEdited={selectedCellPanel.isEdited}
                                    onClear={() =>
                                        selectedValueCell &&
                                        selectedCellPanel.current !== null &&
                                        onCellValuesEditedHandler([
                                            {
                                                rowId: selectedValueCell.rowId,
                                                columnId:
                                                    selectedValueCell.columnId,
                                                amount: null,
                                            },
                                        ])
                                    }
                                    onRevert={() =>
                                        selectedValueCell &&
                                        onCellValuesRevertedHandler([
                                            {
                                                rowId: selectedValueCell.rowId,
                                                columnId:
                                                    selectedValueCell.columnId,
                                            },
                                        ])
                                    }
                                />
                            )}
                            <ComponentSidebar
                                extractionId={extractionId}
                                projectId={projectId}
                                databaseId={databaseId}
                                gridApi={gridApi}
                                description={sidebarDescription}
                                allFoundTables={allFoundTables}
                                canChangeTable={canChangeTable}
                                onTableChange={({
                                    table,
                                }: {
                                    table: TTableLocation;
                                }) =>
                                    onTableChange({
                                        projectId,
                                        extractionId,
                                        table,
                                        databaseId,
                                    })
                                }
                                onLlmReExtract={
                                    isAiExtractionEnabled &&
                                    fileName?.toLowerCase().endsWith(".pdf") ===
                                        true
                                        ? () =>
                                              void llmReExtract({
                                                  extractionId,
                                                  projectId,
                                                  databaseId,
                                              })
                                        : undefined
                                }
                                llmReExtractStatus={llmReExtractStatus}
                                isFromLlm={isFromLlm}
                            />
                        </>
                    }
                    topbarRightContent={<ComponentConcurrentUsersIndicator />}
                    isSidebarCollapsed={isSidebarCollapsed}
                    onSidebarToggle={() =>
                        setIsSidebarCollapsed(!isSidebarCollapsed)
                    }
                    onClose={() => {
                        setCloseExtractionModalOpenState(true);
                    }}
                    footerContent={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                padding: "16px 80px 16px 16px",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            width: "max-content",
                                            marginRight: "8px",
                                        }}
                                    >
                                        Edit Table:
                                    </Typography>
                                    <Toggle
                                        checked={editMode !== "off"}
                                        onClick={() =>
                                            setEditMode(
                                                editMode !== "off"
                                                    ? "off"
                                                    : "rows",
                                            )
                                        }
                                        data-testid={testIds.toggleEdit}
                                    />
                                </Box>
                                {editMode !== "off" ? (
                                    <>
                                        <FooterVerticalDivider />
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: "8px",
                                            }}
                                        >
                                            <Button
                                                pattern={
                                                    editMode === "rows"
                                                        ? "primary"
                                                        : "secondary"
                                                }
                                                size={"medium"}
                                                onClick={() =>
                                                    setEditMode("rows")
                                                }
                                                data-testid={
                                                    testIds.buttonEditRows
                                                }
                                            >
                                                Edit Rows
                                            </Button>
                                            <Button
                                                pattern={
                                                    editMode === "columns"
                                                        ? "primary"
                                                        : "secondary"
                                                }
                                                size={"medium"}
                                                onClick={() =>
                                                    setEditMode("columns")
                                                }
                                                data-testid={
                                                    testIds.buttonEditColumns
                                                }
                                            >
                                                Edit Columns
                                            </Button>
                                        </Box>
                                        <FooterVerticalDivider />
                                        {editMode === "rows" ? (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: "nowrap",
                                                        marginRight: "16px",
                                                    }}
                                                >
                                                    {selectedRows.length} Rows
                                                    Selected
                                                </Typography>
                                                <Dropdown
                                                    hideLabel
                                                    options={rowTypeOptions}
                                                    openUpwards={true}
                                                    value={null}
                                                    placeholder="Select Row Type"
                                                    label=""
                                                    disabled={
                                                        selectedRows.length ===
                                                        0
                                                    }
                                                    onChange={rowType => {
                                                        if (rowType) {
                                                            void updateSelectedRowsType(
                                                                extractionId,
                                                                rowType,
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        minWidth: 180,
                                                        marginRight: "16px",
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        minWidth: "fit-content",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <Checkbox
                                                        label="Flip Row Sign (+/-)"
                                                        pattern="secondary"
                                                        disabled={
                                                            selectedRows.length ===
                                                            0
                                                        }
                                                        value="flip-row-sign"
                                                        checked={
                                                            flipRowSignCheckbox.checked
                                                        }
                                                        indeterminate={
                                                            flipRowSignCheckbox.indeterminate
                                                        }
                                                        onChange={() => {
                                                            void flipSelectedRowsSign(
                                                                extractionId,
                                                                flipRowSignCheckbox.indeterminate ===
                                                                    true
                                                                    ? true
                                                                    : !flipRowSignCheckbox.checked,
                                                            );
                                                        }}
                                                    />
                                                </Box>
                                                {showResetChangesButton && (
                                                    <>
                                                        <FooterVerticalDivider />
                                                        <Button
                                                            pattern="danger"
                                                            isGhost={true}
                                                            size={"medium"}
                                                            leadingIcon="rotate-left"
                                                            onClick={() =>
                                                                handleResetToOriginal()
                                                            }
                                                        >
                                                            {selectedRows.length >
                                                            0
                                                                ? "Reset selected"
                                                                : "Reset to original"}
                                                        </Button>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: "nowrap",
                                                        marginRight: "16px",
                                                    }}
                                                >
                                                    {selectedColumns.length}{" "}
                                                    Columns Selected
                                                </Typography>
                                                <Dropdown
                                                    hideLabel
                                                    options={
                                                        columnActionOptions
                                                    }
                                                    openUpwards={true}
                                                    value={null}
                                                    placeholder="Show or Hide Columns"
                                                    label=""
                                                    disabled={
                                                        selectedColumns.length ===
                                                        0
                                                    }
                                                    onChange={action => {
                                                        if (action === "show") {
                                                            void batchSetColumnState(
                                                                extractionId,
                                                                selectedColumns,
                                                                true,
                                                            );
                                                        } else if (
                                                            action === "hide"
                                                        ) {
                                                            void batchSetColumnState(
                                                                extractionId,
                                                                selectedColumns,
                                                                false,
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        minWidth: 220,
                                                        marginRight: "16px",
                                                    }}
                                                />
                                                {hasDisabledColumns && (
                                                    <>
                                                        <FooterVerticalDivider />
                                                        <Button
                                                            pattern="danger"
                                                            isGhost={true}
                                                            size={"medium"}
                                                            leadingIcon="rotate-left"
                                                            onClick={() =>
                                                                void setColumnState(
                                                                    extractionId,
                                                                    true,
                                                                )
                                                            }
                                                        >
                                                            Reset Changes
                                                        </Button>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </>
                                ) : null}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "row",
                                    gap: "16px",
                                }}
                            >
                                <Button
                                    data-testid={testIds.buttonNext}
                                    pattern="primary"
                                    size={"medium"}
                                    style={{
                                        paddingLeft: "40px",
                                        paddingRight: "40px",
                                    }}
                                    onClick={() =>
                                        void onNextClicked({
                                            projectId,
                                            extractionId,
                                            databaseId,
                                        })
                                    }
                                    disabled={disableNextButton}
                                    trailingIcon="chevron-right"
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>
                    }
                >
                    <LayoutFillParentCentered sx={{ flexDirection: "column" }}>
                        {isDraft && <ComponentConcurrentUsersBanner />}
                        {isAccountsReady && isExtractionReady && (
                            <GridMappings
                                editMode={editMode}
                                errorGroups={errorGroups}
                                columnDefsOperatingStatements={
                                    columnDefsOperatingStatements ||
                                    getTypedEmptyArray()
                                }
                                alteredRowsIds={
                                    alteredRowsIds || getTypedEmptyArray()
                                }
                                rowData={rowData || getTypedEmptyArray()}
                                totalRows={totalRows || getTypedEmptyArray()}
                                categoryRows={
                                    categoryRows || getTypedEmptyArray()
                                }
                                optionsAccounts={optionsAccountsWithType}
                                totalNetIncomeOriginal={totalNetIncomeOriginal}
                                totalNetIncomeAltered={totalNetIncomeAltered}
                                extractionId={extractionId}
                                highlightedTargetRowIds={
                                    highlightedTargetRowIds
                                }
                                onChangeAccounts={onChangeAccountsHandler}
                                onChangeRowType={onChangeRowTypeHandler}
                                onFlipRowSign={onFlipRowSignHandler}
                                onSetDocumentTargetRow={
                                    onSetDocumentTargetRowHandler
                                }
                                onCellValuesEdited={onCellValuesEditedHandler}
                                allowValueEditing={allowValueEditing}
                                onValueCellSelected={
                                    allowValueEditing
                                        ? onValueCellSelected
                                        : undefined
                                }
                                onGridReady={onGridMappingsReadyHandler}
                                onFirstDataRendered={
                                    onGridMappingsFirstDataRendered
                                }
                                setSelectedRows={setSelectedRows}
                                selectedColumns={selectedColumns}
                                onToggleColumnSelection={
                                    onToggleColumnSelection
                                }
                                setColumnState={stableSetColumnState}
                            />
                        )}
                    </LayoutFillParentCentered>
                </LayoutFullScreenWithCollapsibleSidebar>
            </>
        );
    };

    ViewExtractionMappings.setUpPageTestDependencies = ({ gridApi }) => {
        (
            window as Window &
                typeof globalThis & { viewExtractionMappingsGridApi: GridApi }
        ).viewExtractionMappingsGridApi = gridApi;
    };
    ViewExtractionMappings.cleanUpPageTestDependencies = () => {
        (
            window as Window &
                typeof globalThis & { viewExtractionMappingsGridApi: null }
        ).viewExtractionMappingsGridApi = null;
    };

    return ViewExtractionMappings;
};
